import { MovementKey, MovementGroups } from './types';
import { Package } from '../../types/package';
import { Flight } from '../../types/flight';
import { Pilgrim } from '../../types/pilgrim';
import { calculateDepartureTime } from './timeUtils';

const createMovementKey = (
  type: string,
  from: string,
  to: string,
  departureDate: string,
  departureTime: string
): string => {
  return `${type}|${from}|${to}|${departureDate}|${departureTime}`;
};

export const groupMovements = (
  packages: Record<string, Package>,
  flights: Record<string, Flight>,
  pilgrims: Record<string, Pilgrim>
): MovementGroups => {
  const groups: MovementGroups = {};

  Object.values(pilgrims).forEach((pilgrim) => {
    const pkg = Object.values(packages).find(
      (p) => p.name === pilgrim.package_nickName
    );
    const flight = Object.values(flights).find(
      (f) => f.pnrNumber === pilgrim.PNR_NO
    );

    if (!pkg || !flight) return;

    // Group arrival movements
    const arrivalKey = createMovementKey(
      'arrival',
      flight.outboundArrival,
      pkg.mainMakkahHotel,
      flight.arrivalDate,
      flight.outboundArrivalTime
    );

    if (!groups[arrivalKey]) {
      groups[arrivalKey] = { pilgrims: [], package: pkg, flight };
    }
    groups[arrivalKey].pilgrims.push(pilgrim);

    // Group departure movements
    const departureKey = createMovementKey(
      'departure',
      pkg.shiftingMakkahHotel || pkg.mainMakkahHotel,
      flight.returnDeparture,
      flight.returnDate,
      calculateDepartureTime(flight.returnDepartureTime, 3)
    );

    if (!groups[departureKey]) {
      groups[departureKey] = { pilgrims: [], package: pkg, flight };
    }
    groups[departureKey].pilgrims.push(pilgrim);

    // Group hotel transfer movements (if shifting hotel exists)
    if (pkg.shiftingMakkahHotel) {
      const hotelTransferKey = createMovementKey(
        'hotel_transfer',
        pkg.mainMakkahHotel,
        pkg.shiftingMakkahHotel,
        pkg.shiftingMakkahStartDate,
        '12:00:00 PM' // Default time for hotel transfers
      );

      if (!groups[hotelTransferKey]) {
        groups[hotelTransferKey] = { pilgrims: [], package: pkg, flight };
      }
      groups[hotelTransferKey].pilgrims.push(pilgrim);
    }

    // Group camp movements (if camp exists)
    if (pkg.camp) {
      // Camp departure
      const campDepartureKey = createMovementKey(
        'camp_departure',
        pkg.shiftingMakkahHotel || pkg.mainMakkahHotel,
        pkg.camp,
        '10/01/2025', // Fixed camp dates
        '12:00:00 PM'
      );

      if (!groups[campDepartureKey]) {
        groups[campDepartureKey] = { pilgrims: [], package: pkg, flight };
      }
      groups[campDepartureKey].pilgrims.push(pilgrim);

      // Camp return
      const campReturnKey = createMovementKey(
        'camp_return',
        pkg.camp,
        pkg.shiftingMakkahHotel || pkg.mainMakkahHotel,
        '15/01/2025', // Fixed camp dates
        '12:00:00 PM'
      );

      if (!groups[campReturnKey]) {
        groups[campReturnKey] = { pilgrims: [], package: pkg, flight };
      }
      groups[campReturnKey].pilgrims.push(pilgrim);
    }

    // Group Madinah transfer movements (if Madinah hotel exists)
    if (pkg.madinahHotel) {
      const madinahTransferKey = createMovementKey(
        'hotel_transfer_madinah',
        pkg.shiftingMakkahHotel || pkg.mainMakkahHotel,
        pkg.madinahHotel,
        pkg.madinahStartDate,
        '12:00:00 PM'
      );

      if (!groups[madinahTransferKey]) {
        groups[madinahTransferKey] = { pilgrims: [], package: pkg, flight };
      }
      groups[madinahTransferKey].pilgrims.push(pilgrim);
    }
  });

  return groups;
};
